import { useState, useEffect, useCallback } from 'react';
import { Loader } from './LoadingSpinner';
import HistoryTimeline from './HistoryTimeline';
import HistoryFilters from './HistoryFilters';
import ExcludeEventModal from './ExcludeEventModal';
import { Clock, AlertCircle, BarChart3, TrendingUp } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { events as eventsApi } from '../../services/apiService';
import { toast } from 'react-hot-toast';

/**
 * Универсальный компонент для отображения истории изменений (событий)
 * Используется для Talent и JobApplication
 */
const INITIAL_FILTERS = { eventTypes: [], userIds: [], dateFrom: '', dateTo: '' };

const HistoryTab = ({ entityId, fetchEventsFunction, fetchStatsFunction }) => {
  const { hasPermission } = useAuth();
  const canCancel = hasPermission('talent-service', 'events', 'cancel');

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [eventsPagination, setEventsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [actionLoading, setActionLoading] = useState(null);
  const [excludeTargetId, setExcludeTargetId] = useState(null);

  // Statistics state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Load events (change history)
  const fetchEvents = async (page = 1) => {
    if (!entityId || !fetchEventsFunction) return;

    try {
      setEventsLoading(true);
      setEventsError(null);

      const response = await fetchEventsFunction(entityId, {
        page,
        limit: 20,
        ...(filters.eventTypes.length && { event_types: filters.eventTypes }),
        ...(filters.userIds.length    && { user_ids: filters.userIds }),
        ...(filters.dateFrom          && { date_from: filters.dateFrom }),
        ...(filters.dateTo            && { date_to: filters.dateTo }),
      });

      setEvents(response.data);
      setEventsPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEventsError('Error loading change history');
    } finally {
      setEventsLoading(false);
    }
  };

  // Load statistics
  const fetchStats = async () => {
    if (!entityId || !fetchStatsFunction) return;

    try {
      setStatsLoading(true);
      const response = await fetchStatsFunction(entityId, 30);
      setStats(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Don't show error for stats, just don't display them
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setEventsPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCancel = useCallback((eventId) => {
    setExcludeTargetId(eventId);
  }, []);

  const handleExcludeConfirm = useCallback(async (reason) => {
    const eventId = excludeTargetId;
    if (!eventId) return;
    setActionLoading(eventId);
    try {
      await eventsApi.cancelEvent(eventId, reason || null);
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, is_cancelled: true, cancel_reason: reason || null }
        : e
      ));
      toast.success('Event excluded from statistics');
      setExcludeTargetId(null);
    } catch {
      toast.error('Failed to exclude event');
    } finally {
      setActionLoading(null);
    }
  }, [excludeTargetId]);

  const handleUncancel = useCallback(async (eventId) => {
    setActionLoading(eventId);
    try {
      await eventsApi.uncancelEvent(eventId);
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, is_cancelled: false, cancel_reason: null, cancelled_by: null }
        : e
      ));
      toast.success('Event restored to statistics');
    } catch {
      toast.error('Failed to restore event');
    } finally {
      setActionLoading(null);
    }
  }, []);

  // Load events when component mounts, page or filters change
  useEffect(() => {
    if (entityId) {
      fetchEvents(eventsPagination.page);
    }
  }, [entityId, eventsPagination.page, filters]);

  // Load statistics on mount
  useEffect(() => {
    if (entityId && fetchStatsFunction) {
      fetchStats();
    }
  }, [entityId]);

  return (
    <>
    <ExcludeEventModal
      isOpen={excludeTargetId !== null}
      onClose={() => setExcludeTargetId(null)}
      onConfirm={handleExcludeConfirm}
      isLoading={actionLoading === excludeTargetId && excludeTargetId !== null}
    />
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
          Change History
        </h3>
        {eventsPagination.total > 0 && (
          <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Total events: {eventsPagination.total}
          </span>
        )}
      </div>

      <HistoryFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Event Statistics */}
      {stats && stats.eventsByType && stats.eventsByType.length > 0 && (
        <div className="mb-6 p-4 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary">
          <div className="flex items-center mb-3">
            <BarChart3 className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
            <h4 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              Event Statistics (Last 30 Days)
            </h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {stats.eventsByType.map((stat) => (
              <div
                key={stat.event_type}
                className="flex flex-col p-3 bg-light-bg-primary dark:bg-dark-bg-primary rounded-md border border-light-border-secondary dark:border-dark-border-secondary"
              >
                <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1">
                  {stat.event_type}
                </span>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-1" />
                  <span className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                    {stat.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {eventsLoading ? (
        <Loader variant="component" size="md" fullHeight={false} />
      ) : eventsError ? (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{eventsError}</p>
          <button
            onClick={() => fetchEvents(eventsPagination.page)}
            className="mt-2 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : events && events.length > 0 ? (
        <div>
          {/* Use HistoryTimeline for beautiful display */}
          <div className="max-h-96 overflow-y-auto">
            <HistoryTimeline
              events={events}
              maxEvents={events.length}
              canCancel={canCancel}
              onCancel={handleCancel}
              onUncancel={handleUncancel}
              actionLoading={actionLoading}
            />
          </div>

          {/* Pagination */}
          {eventsPagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-light-border-primary dark:border-dark-border-primary">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const newPage = eventsPagination.page - 1;
                    setEventsPagination(prev => ({ ...prev, page: newPage }));
                  }}
                  disabled={eventsPagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-md hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
                  Page {eventsPagination.page} of {eventsPagination.totalPages}
                </span>

                <button
                  onClick={() => {
                    const newPage = eventsPagination.page + 1;
                    setEventsPagination(prev => ({ ...prev, page: newPage }));
                  }}
                  disabled={eventsPagination.page === eventsPagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-md hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>

              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Showing {events.length} of {eventsPagination.total} events
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <p className="text-light-text-tertiary dark:text-dark-text-tertiary">
            Change history is empty
          </p>
        </div>
      )}
    </div>
    </>
  );
};

export default HistoryTab;
